import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthLayout } from '../layouts/AuthLayout';
import { LoginPage } from '../pages/LoginPage';
import { HomePage } from '../pages/HomePage';
import { ProtectedRoute } from './ProtectedRoute';
import { AdminLayout } from '../layouts/AdminLayout';
import { GeoBlockPage } from '../features/geo-access/GeoBlockPage';
import { GameServersPage } from '../features/game-servers/GameServersPage';
import { UsersPage } from '../features/users/UsersPage';
import { UserDetailPage } from '../features/users/UserDetailPage';
import { CatalogPage } from '../features/catalog/CatalogPage';
import { RewardHistoryPage } from '../features/reward-history/RewardHistoryPage';
import { TransactionsPage } from '../features/transactions/TransactionsPage';
import { PaySegmentsPage } from '../features/pay-segments/PaySegmentsPage';
import { VIPConfigsPage } from '../features/vip/VIPConfigsPage';
import { GlobalConfigPage } from '../features/global-config/GlobalConfigPage';
import { RoomsConfigsPage } from '../features/rooms-config/RoomsConfigsPage';
import { BotFillConfigPage } from '../features/bot-fill/BotFillConfigPage';
import { EventsPage } from '../features/events/EventsPage';
import { MonitorPage } from '../features/monitor/MonitorPage';
import { AlertManagement } from '../features/monitor/AlertManagement';
import { MetricsPage } from '../features/metrics/MetricsPage';
import { isMonitorAvailable } from '../config/env';
import { useAuth } from '../auth/AuthProvider';

const CatchAllRedirect: React.FC = () => {
  const { isAuthenticated, isRestoring } = useAuth();
  if (isRestoring) {
    return null;
  }
  return (
    <Navigate to={isAuthenticated ? '/home' : '/login'} replace />
  );
};

const MonitorRoute: React.FC = () =>
  isMonitorAvailable() ? <MonitorPage /> : <Navigate to="/geo-access/rules" replace />;

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<AdminLayout />}>
          <Route path="/home" element={<HomePage />} />
          <Route path="/geo-access/rules" element={<GeoBlockPage />} />
          <Route
            path="/geo-access/decision-logs"
            element={<GeoBlockPage />}
          />
          <Route
            path="/geo-access"
            element={<Navigate to="/geo-access/rules" replace />}
          />
          <Route path="/game-servers" element={<GameServersPage />} />
          <Route path="/users" element={<UsersPage key="users" fixedRole="user" />} />
          <Route path="/users/:userId" element={<UserDetailPage />} />
          <Route path="/administrators" element={<UsersPage key="administrators" fixedRole="admin" />} />
          <Route path="/administrators/:userId" element={<UserDetailPage />} />
          <Route path="/system-alert" element={<AlertManagement />} />
          <Route path="/catalog" element={<CatalogPage />} />
          <Route path="/daily-support-history" element={<RewardHistoryPage key="daily-support" kind="daily-support" />} />
          <Route path="/rewarded-ads-history" element={<RewardHistoryPage key="rewarded-ads" kind="rewarded-ads" />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/pay-segments" element={<PaySegmentsPage />} />
          <Route path="/vip-configs" element={<VIPConfigsPage />} />
          <Route path="/global-config" element={<GlobalConfigPage />} />
          <Route path="/rooms-config" element={<RoomsConfigsPage />} />
          <Route path="/bot-fill-config" element={<BotFillConfigPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/monitor" element={<MonitorRoute />} />
          <Route path="/metrics" element={<MetricsPage />} />
        </Route>
      </Route>

      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  );
};
